import { ColumnTypes } from '../../../../src/components/table/columnTypes';
import { List, OrderedMap } from 'immutable';
import { IReportColumn } from '@opengov/reporting-types/src/reportColumns-immer';
import {
  BooleanOperators,
  FilterOperators,
  IColumnFilter,
  IColumnFilterBooleanOperator
} from '@opengov/reporting-types/src/reportFilters';

import { getTableColumnTypeFromReportColumnType } from '../../../common/helpers/semanticTypeToColumnType';

const findBooleanOperator = ({
  booleanOperators,
  columnName
}: {
  booleanOperators: List<IColumnFilterBooleanOperator>;
  columnName: string;
}) => {
  const defaultBooleanOperator = BooleanOperators.Or;

  if (!booleanOperators || booleanOperators.size === 0) {
    return defaultBooleanOperator;
  }

  const matchingColumnBooleanOperator = booleanOperators.find((filter) => filter.columnName === columnName);

  return (matchingColumnBooleanOperator && matchingColumnBooleanOperator.booleanOperator) || defaultBooleanOperator;
};

const getFilterBooleanState = ({
  columnName,
  defaultOperator,
  filterBooleanOperators,
  filterOptions,
  filters,
  rowCount,
  bypassMultiFilterLimit
}: {
  columnName: string;
  defaultOperator: string;
  filterBooleanOperators: IColumnFilterBooleanOperator[];
  filterOptions: OrderedMap<string, number>;
  filters: List<IColumnFilter>;
  rowCount: number;
  bypassMultiFilterLimit: boolean;
}) => {
  return {
    rowCount,
    booleanOperator: findBooleanOperator({
      columnName,
      booleanOperators: List(filterBooleanOperators)
    }),
    queryOperator: defaultOperator,
    filters:
      filters &&
      filters
        .filter((filter) => filter.columnName === columnName)
        .map((textFilter) => {
          return {
            text: textFilter.value,
            operator: textFilter.operator
          };
        }),
    filterOptions,
    defaultOperator,
    bypassMultiFilterLimit
  };
};

const getFilterTextMultiState = ({
  columnName,
  defaultOperator,
  filterBooleanOperators,
  filterOptions,
  filters,
  rowCount,
  bypassMultiFilterLimit
}: {
  columnName: string;
  defaultOperator: string;
  filterBooleanOperators: IColumnFilterBooleanOperator[];
  filterOptions: OrderedMap<string, number>;
  filters: List<IColumnFilter>;
  rowCount: number;
  bypassMultiFilterLimit: boolean;
}) => {
  return {
    rowCount,
    booleanOperator: findBooleanOperator({
      columnName,
      booleanOperators: List(filterBooleanOperators)
    }),
    queryOperator: defaultOperator,
    filters:
      filters &&
      filters
        .filter((filter) => filter.columnName === columnName)
        .map((textFilter) => {
          return {
            text: textFilter && textFilter.value ? textFilter.value : '',
            operator: textFilter.operator
          };
        }),
    filterOptions,
    defaultOperator,
    bypassMultiFilterLimit
  };
};

const getNumberFilterState = ({ columnName, filters }: { columnName: string; filters: List<IColumnFilter> }) => {
  let minValue;
  let maxValue;

  const minFilter =
    filters &&
    filters.find((filter) => filter.columnName === columnName && filter.operator === FilterOperators.Greater);
  const maxFilter =
    filters && filters.find((filter) => filter.columnName === columnName && filter.operator === FilterOperators.Lesser);
  const equalFilter =
    filters && filters.find((filter) => filter.columnName === columnName && filter.operator === FilterOperators.Equal);

  if (equalFilter) {
    minValue = parseFloat(equalFilter.value as string);
    maxValue = parseFloat(equalFilter.value as string);
  } else {
    minValue = minFilter && parseFloat(minFilter.value as string);
    maxValue = maxFilter && parseFloat(maxFilter.value as string);
  }

  return {
    minValue,
    maxValue
  };
};

export const getDateFilterState = ({ columnName, filters }: { columnName: string; filters: List<IColumnFilter> }) => {
  let minValue;
  let maxValue;

  const minFilter =
    filters &&
    filters.find((filter) => filter.columnName === columnName && filter.operator === FilterOperators.Greater);
  const maxFilter =
    filters && filters.find((filter) => filter.columnName === columnName && filter.operator === FilterOperators.Lesser);
  const equalFilter =
    filters && filters.find((filter) => filter.columnName === columnName && filter.operator === FilterOperators.Equal);

  if (equalFilter) {
    minValue = equalFilter.value;
    maxValue = equalFilter.value;
  } else {
    minValue = minFilter && minFilter.value;
    maxValue = maxFilter && maxFilter.value;
  }

  return {
    minValue,
    maxValue
  };
};

export const getFilterState = ({
  column,
  defaultOperator,
  filterBooleanOperators,
  filterOptions,
  filters,
  rowCount,
  bypassMultiFilterLimit
}: {
  column: IReportColumn;
  defaultOperator?: string;
  filterBooleanOperators: IColumnFilterBooleanOperator[];
  filterOptions: OrderedMap<string, number>;
  filters: List<IColumnFilter>;
  rowCount: number;
  bypassMultiFilterLimit: boolean;
}) => {
  const columnType = getTableColumnTypeFromReportColumnType(column);

  switch (columnType) {
    case ColumnTypes.Boolean:
      return getFilterBooleanState({
        columnName: column.name,
        defaultOperator,
        filterBooleanOperators,
        filterOptions,
        filters,
        rowCount,
        bypassMultiFilterLimit
      });
    case ColumnTypes.Text:
      return getFilterTextMultiState({
        columnName: column.name,
        defaultOperator,
        filterBooleanOperators,
        filterOptions,
        filters,
        rowCount,
        bypassMultiFilterLimit
      });
    case ColumnTypes.Year:
    case ColumnTypes.Date:
      return getDateFilterState({ columnName: column.name, filters });
    case ColumnTypes.Period:
    case ColumnTypes.Percent:
    case ColumnTypes.Currency:
    case ColumnTypes.Number:
      return getNumberFilterState({ columnName: column.name, filters });
    default:
  }
};
